<script lang="ts">
	import Router from 'svelte-spa-router';
	import { format } from '../services/i18n';
	import { location } from 'svelte-spa-router';
	import { LoginLayoutV2 } from '@Shelly/shelly-design-system';
	import { user_settings } from 'src/stores/account/user';
	import { initializing_language } from 'src/stores/session/preferences';
	import { LANG_DICT, TIMEZONE_LANG_MAP } from 'src/constants';
	import { changeLanguage } from 'src/components/Profiles/profiles_helpers';
	import { onMount } from 'svelte';
	import { acquireScrollbarWhite } from 'src/helpers/scrollbar_white_helpers';
	import { formatLanguageIso } from 'src/helpers/language_helpers';
	import type { LangaugeDropdownItem } from 'src/types/interfaces';

	import LoginV2 from './LoginV2.svelte';
	import SignupV2 from './SignupV2.svelte';
	import EmailVerificationV2 from './EmailVerificationV2.svelte';
	import ChangeEmailV2 from './ChangeEmailV2.svelte';
	import RequestPasswordResetV2 from './RequestPasswordResetV2.svelte';
	import PasswordResetV2 from './PasswordResetV2.svelte';
	import RequestDeleteAccountV2 from './RequestDeleteAccountV2.svelte';
	import AccountDeleteV2 from './AccountDeleteV2.svelte';
	import FastLoginV2 from './FastLoginV2.svelte';
	import InstallerLoginV2 from './InstallerLoginV2.svelte';
	import SubAccountSignUpV2 from './SubAccountSignUpV2.svelte';
	import LangDropdownV2 from 'src/components/HelperComponents/LangDropdownV2.svelte';

	const routes = {
		'/login': LoginV2,
		'/signup': SignupV2,
		'/email-verification': EmailVerificationV2,
		'/change-email': ChangeEmailV2,
		'/request-password-reset': RequestPasswordResetV2,
		'/password-reset': PasswordResetV2,
		'/request-account-delete': RequestDeleteAccountV2,
		'/account-delete': AccountDeleteV2,
		'/fast-login': FastLoginV2,
		'/installer-login': InstallerLoginV2,
		'/register_subaccount/:token/:email': SubAccountSignUpV2,
	};
	let initialLanguage: string;
	let dropdownItemsData: LangaugeDropdownItem[];
	let showDropdownMenu = false;
	let currentLangIso: string;

	const onLanguageChange = (dropdownItem: Partial<LangaugeDropdownItem>) => {
		currentLangIso = formatLanguageIso(dropdownItem.language);
		showDropdownMenu = false;

		changeLanguage(dropdownItem);
	};

	$: if ($location) {
		showDropdownMenu = false;
	}

	const setLanguageDropdownItems = () => {
		const languages = Object.entries(LANG_DICT);
		const dropdownItems = [];

		for (const [iso, name] of languages) {
			const dropdownItem = {
				title: name,
				subtitle: formatLanguageIso(iso),
				language: iso,
			};

			dropdownItems.push(dropdownItem);
		}

		return dropdownItems;
	};

	onMount(() => acquireScrollbarWhite());

	onMount(() => {
		dropdownItemsData = setLanguageDropdownItems();

		/**
		 * Apply the timezone-based default ONLY when the user has no existing
		 * language preference (e.g. very first launch). Otherwise we would
		 * overwrite a language the user explicitly picked on the login/register
		 * screen every time this layout remounts (incl. after a refresh).
		 */
		const existingLang = $user_settings?.lang || $initializing_language;

		if (existingLang) {
			currentLangIso = formatLanguageIso(existingLang);
			return;
		}

		initialLanguage = TIMEZONE_LANG_MAP[new Intl.DateTimeFormat().resolvedOptions().timeZone];

		if (initialLanguage) {
			currentLangIso = initialLanguage.toUpperCase();
			changeLanguage({ language: initialLanguage });
		} else {
			currentLangIso = formatLanguageIso();
		}
	});
</script>

<LoginLayoutV2
	backgroundImgSrc="images/loginBackground.png"
	subtitle={$format('login_welcome_subtitle', {
		default: 'Welcome to the Shelly Experience',
	})}
	logoUrl="images/shelly-logo.svg"
>
	<svelte:fragment slot="title">
		{@html $format('login_tagline_title', {
			default: 'Explore the Possibilities',
		})}
	</svelte:fragment>
	<div slot="form">
		{#if $location !== '/fast-login'}
			<div class="lng-bar text-right">
				<LangDropdownV2
					bind:showDropdown={showDropdownMenu}
					dropdownItems={dropdownItemsData}
					lang={currentLangIso}
					selectedLang={$user_settings.lang}
					on:change-lang={(e) => {
						onLanguageChange(e.detail);
					}}
				/>
			</div>
		{/if}
		<Router {routes} />
	</div>
</LoginLayoutV2>

<style>
	.lng-bar {
		float: right;
		margin: -2px 0 var(--spacing-s) var(--spacing-s);
	}
</style>
